import { DecisionSubViews, DecisionViews } from 'pages/Decision/constants'
import { useCallback, useMemo } from 'react'
import { useGetApplicationStatusQuery } from 'redux/api'
import { useGetCreditProgramQuery } from 'redux/api/creditApi'
import {
  creditProgramIDSelector,
  programSelector,
  setCreditProgramID,
  setLoanParams,
  setMainView,
  setProgram,
  setProperty,
  setSubView
} from 'redux/store/slices/decisionSlice'
import { getActiveCreditApplication, getCurrentFlowId } from 'redux/store/slices/userSlice'
import { ApplicationProgramCode } from 'share/types'
import { ScoringDefinedCreditProgram } from 'share/types/api/credits'
import { isScoringOptionsArray, isScoringOptionsObject } from 'utils/type-guards'

import { useAppDispatch, useAppSelector } from '.'

const getSubViewByProgram = (program: ScoringDefinedCreditProgram) => {
  let viewToChange: DecisionSubViews = DecisionSubViews['CHOOSE-LOAN-PARAMS']
  if (program.with_pledges) {
    viewToChange = DecisionSubViews['CHOOSE-PROPERTY-PLEDGE']
    const isProgramDummy = Object.keys(program.options).length === 0
    if (isProgramDummy) {
      viewToChange = DecisionSubViews['UPGRADE-OWN-WITH-PLEDGE-PROGRAM']
    }
  }
  return viewToChange
}

const useCreditDecision = () => {
  // Tools
  const dispatch = useAppDispatch()

  // Selectors
  const { id: creditID } = useAppSelector(getActiveCreditApplication)
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const selectedSubProgramId = useAppSelector(creditProgramIDSelector)
  const selectedProgram = useAppSelector(programSelector)

  // Queries
  const programs = useGetCreditProgramQuery(creditID as string, {
    skip: !creditID
  })

  const status = useGetApplicationStatusQuery(currentFlowId as string, {
    skip: !currentFlowId
  })

  // Computed
  const isPledgeProgramsPresent = useMemo(
    () => programs.isSuccess && programs.data.some(program => program.programs.some(p => p.with_pledges)),
    [programs.data, programs.isSuccess]
  )

  const programsByProgramCode = useMemo(() => {
    if (!programs) return []

    const foundedPrograms = programs.data?.find(program => program.program_code === selectedProgram)
    if (!foundedPrograms) return []
    return foundedPrograms.programs
  }, [programs, selectedProgram])

  const selectedSubProgram = useMemo(() => {
    if (!programs) return undefined
    return programsByProgramCode.find(program => program.id === selectedSubProgramId)
  }, [programs, programsByProgramCode, selectedSubProgramId])

  const is3rdPartyPledgerApplied = useMemo(() => {
    if (!status.isSuccess) return false
    return !!status.data.third_party_pledger
  }, [status.data?.third_party_pledger, status.isSuccess])

  // Handlers
  const onMainViewChange = useCallback(
    (view: DecisionViews) => {
      dispatch(setMainView(view))
      window.scrollTo(0, 0)
    },
    [dispatch]
  )

  const onSubViewChange = useCallback(
    (view: DecisionSubViews) => {
      dispatch(setSubView(view))
      window.scrollTo(0, 0)
    },
    [dispatch]
  )

  const prepareSubView = useCallback(
    (id: string) => {
      const program = programsByProgramCode?.find(program => program.id === id)
      if (!program) return
      dispatch(setCreditProgramID(id))
      dispatch(setProperty(undefined))
      if (isScoringOptionsObject(program)) {
        const amount = selectedProgram === 'DAMU' ? program.max_loan_amount : program.options.credit_params.loan_amount
        dispatch(
          setLoanParams({
            amount: '' + Number(amount),
            period: '' + +program.max_period
          })
        )
      }
      if (isScoringOptionsArray(program)) {
        const maxPeriodObj = program.options.reduce(
          (max, obj) => (obj.credit_params.period > max.credit_params.period ? obj : max),
          program.options[0]
        )
        if (!maxPeriodObj) throw new Error('Option is not defined')
        const {
          credit_params: { period, loan_amount }
        } = maxPeriodObj
        dispatch(
          setLoanParams({
            amount: '' + +loan_amount,
            period: '' + +period
          })
        )
      }
      const view = getSubViewByProgram(program)
      dispatch(setSubView(view))
      window.scrollTo(0, 0)
    },
    [dispatch, programsByProgramCode, selectedProgram]
  )

  const prepareLoanParamsView = useCallback(
    (code: ApplicationProgramCode) => {
      if (!programs.isSuccess || !programs) return
      const foundedProgramCode = programs.data.find(program => program.program_code === code)
      if (!foundedProgramCode) return
      const firstProgram = foundedProgramCode.programs.at(0)
      if (!firstProgram) return
      dispatch(setProgram(code))
      if (isScoringOptionsObject(firstProgram)) {
        const amount =
          selectedProgram === 'DAMU' ? firstProgram.max_loan_amount : firstProgram.options.credit_params.loan_amount
        dispatch(
          setLoanParams({
            amount: '' + Number(amount),
            period: '' + +firstProgram.max_period
          })
        )
      }
      if (isScoringOptionsArray(firstProgram)) {
        const maxPeriodObj = firstProgram.options.reduce(
          (max, obj) => (obj.credit_params.period > max.credit_params.period ? obj : max),
          firstProgram.options[0]
        )
        if (!maxPeriodObj) throw new Error('Option is not defined')
        const {
          credit_params: { period, loan_amount }
        } = maxPeriodObj
        dispatch(
          setLoanParams({
            amount: '' + +loan_amount,
            period: '' + +period
          })
        )
      }
      const view = getSubViewByProgram(firstProgram)
      dispatch(setCreditProgramID(firstProgram.id))
      dispatch(setProperty(undefined))
      dispatch(setSubView(view))
      dispatch(setMainView(DecisionViews['LOAN-CONDITIONS']))
      window.scrollTo(0, 0)
    },
    [dispatch, programs, selectedProgram]
  )

  return {
    programs,
    status,
    isPledgeProgramsPresent,
    programsByProgramCode,
    selectedSubProgram,
    is3rdPartyPledgerApplied,
    prepareLoanParamsView,
    prepareSubView,
    onMainViewChange,
    onSubViewChange
  }
}

export { useCreditDecision }
